import { useState, useCallback } from 'react'
import { getAuthService } from '../services/factory'
import { User, RegisterPayload } from '../services/authService'

export type AuthStatus = 'idle' | 'submitting' | 'success' | 'error'

export interface AuthState {
  status: AuthStatus
  error: string | null
}

export const useAuthState = () => {
  const [state, setState] = useState<AuthState>({
    status: 'idle',
    error: null
  })

  const login = useCallback(async (email: string, password: string): Promise<User | null> => {
    // Transition to submitting state
    setState({
      status: 'submitting',
      error: null
    })

    try {
      const authService = getAuthService()
      const result = await authService.login({ email, password })

      if (result.success && result.user) {
        // Transition to success state
        setState({
          status: 'success',
          error: null
        })
        return result.user
      } else {
        // Transition to error state
        const errorMessage = result.errors?.join(', ') || 'Login failed'
        setState({
          status: 'error',
          error: errorMessage
        })
        return null
      }
    } catch (error) {
      // Transition to error state on exception
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
      setState({
        status: 'error',
        error: errorMessage
      })
      return null
    }
  }, [])

  const register = useCallback(async (payload: RegisterPayload): Promise<User | null> => {
    setState({
      status: 'submitting',
      error: null
    })

    try {
      const authService = getAuthService()
      const result = await authService.register(payload)

      if (result.success && result.user) {
        setState({
          status: 'success',
          error: null
        })
        return result.user
      } else {
        const errorMessage = result.errors?.join(', ') || 'Registration failed'
        setState({
          status: 'error',
          error: errorMessage
        })
        return null
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
      setState({
        status: 'error',
        error: errorMessage
      })
      return null
    }
  }, [])

  const resetPassword = useCallback(async (email: string): Promise<boolean> => {
    setState({
      status: 'submitting',
      error: null
    })

    try {
      const authService = getAuthService()
      const result = await authService.resetPassword(email)

      if (result.success) {
        setState({
          status: 'success',
          error: null
        })
        return true
      } else {
        const errorMessage = result.errors?.join(', ') || 'Password reset failed'
        setState({
          status: 'error',
          error: errorMessage
        })
        return false
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
      setState({
        status: 'error',
        error: errorMessage
      })
      return false
    }
  }, [])

  const requestPasswordReset = useCallback(async (email: string): Promise<boolean> => {
    setState({
      status: 'submitting',
      error: null
    })

    try {
      const authService = getAuthService()
      const result = await authService.requestPasswordReset(email)

      if (result.success) {
        setState({
          status: 'success',
          error: null
        })
        return true
      } else {
        const errorMessage = result.errors?.join(', ') || 'Password reset request failed'
        setState({
          status: 'error',
          error: errorMessage
        })
        return false
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
      setState({
        status: 'error',
        error: errorMessage
      })
      return false
    }
  }, [])

  const resetPasswordWithToken = useCallback(async (token: string, newPassword: string): Promise<boolean> => {
    setState({
      status: 'submitting',
      error: null
    })

    try {
      const authService = getAuthService()
      const result = await authService.resetPasswordWithToken(token, newPassword)

      if (result.success) {
        setState({
          status: 'success',
          error: null
        })
        return true
      } else {
        const errorMessage = result.errors?.join(', ') || 'Password reset failed'
        setState({
          status: 'error',
          error: errorMessage
        })
        return false
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
      setState({
        status: 'error',
        error: errorMessage
      })
      return false
    }
  }, [])

  const reset = useCallback(() => {
    setState({
      status: 'idle',
      error: null
    })
  }, [])

  return {
    state,
    login,
    register,
    resetPassword,
    requestPasswordReset,
    resetPasswordWithToken,
    reset
  }
}